<template>
  <div class="size-guide">
    <template v-if="guide">
      <h3
        class="size-guide__header"
        v-text="$string('product.extra.size_guide')"
      />

      <div
        v-if="guide.title"
        class="size-guide__title"
        v-text="guide.title"
      />

      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        class="size-guide__diagram"
        :src="guide.diagram"
        alt=""
      >

      <template v-if="guide.measurements_cm || guide.measurements_in">
        <h4
          class="size-guide__subtitle"
          v-text="$string('size_guide.by_size')"
        />

        <div
          class="size-guide__info"
          v-text="$string('size_guide.by_size_note')"
        />

        <div class="size-guide__tabs">
          <button
            class="size-guide__tab"
            :class="{'active': tabScope === 'in'}"
            type="button"
            :disabled="!guide.measurements_in"
            @click="tabScope = 'in'"
          >
            {{ $string('size_guide.in') }}
          </button>

          <button
            class="size-guide__tab"
            :class="{'active': tabScope === 'cm'}"
            type="button"
            :disabled="!guide.measurements_cm"
            @click="tabScope = 'cm'"
          >
            {{ $string('size_guide.cm') }}
          </button>
        </div>

        <table
          v-for="(scope, index) of tabScopes"
          v-show="tabScope === scope.scope"
          :key="index"
          class="size-guide__table"
          cellspacing="0"
        >
          <tr
            v-for="(row, rowIndex) of guide[scope.key].data"
            :key="rowIndex"
          >
            <td
              v-for="(cell, cellIndex) of row"
              :key="cellIndex"
              v-text="cell"
            />
          </tr>
        </table>
      </template>

      <h4
        class="size-guide__subtitle"
        v-text="$string('size_guide.how_to')"
      />

      <div
        class="size-guide__info size-guide__how-to-info"
        v-text="$string('size_guide.how_to_note')"
      />

      <!-- eslint-disable-next-line vue/no-restricted-html-elements -->
      <img
        v-if="guide.how_to_diagram"
        class="size-guide__how-to-diagram"
        :src="guide.how_to_diagram"
        alt=""
      >

      <div class="size-guide__instructions">
        <template
          v-for="n of 5"
          :key="n"
        >
          <h4
            v-if="guide[`how-to-${n}-title`]"
            class="size-guide__subtitle size-guide__instruction-title"
            v-text="guide[`how-to-${n}-title`]"
          />
          <div
            v-if="guide[`how-to-${n}-title`]"
            class="size-guide__info size-guide__instruction-content"
            v-text="guide[`how-to-${n}-content`]"
          />
        </template>
      </div>

      <div class="size-guide__footer">
        {{ $string('size_guide.footer') }}
        <a
          @click="openChat"
          v-text="$string('size_guide.chat')"
        />
      </div>
    </template>

    <div
      v-else
      class="size-guide__loading"
    >
      <icon-loading />
    </div>
  </div>
</template>

<script>

/**
 * Vue: Size guide (size-guide)
 * -----------------------------------------------------------------------------
 * Display product sizing information.
 * @param {Object} sizeGuide
 *
 */
import Papa from 'papaparse'
import IconLoading from '~icons/misc/loading.svg'

export default {
  name: 'SizeGuide',

  components: {
    IconLoading,
  },

  props: {
    sizeGuides: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      guideKeys: [],
      guides: [],
      sizeGuide: {},
      tabScope: 'in',
      tabScopes: [
        {
          key: 'measurements_in',
          scope: 'in',
        },
        {
          key: 'measurements_cm',
          scope: 'cm',
        },
      ],
    }
  },

  computed: {

    /**
     * Compute active size guide.
     * @returns {Object}
     */
    guide() {
      if (!this.guides.length) {
        return false
      }

      const found = this.guides.find((guide) => guide.id === this.sizeGuide?.value)

      if (!found) {
        return false
      }

      return found
    },
  },

  mounted() {
    cnvs.EventBus.listen('size-guide:toggle', (response) => {
      this.sizeGuide = response.sizeGuide
      this.getSizeGuide(this.sizeGuide.value)
    })
  },

  methods: {

    /**
     * Format size guide with dynamic data.
     * @param {String} sizeGuideId
     */
    async getSizeGuide(sizeGuideId) {
      const data = this.sizeGuides.find((guide) => {
        return guide.id === sizeGuideId
      })

      if (!data) {
        return
      }

      if (this.guideKeys.includes(data.id)) {
        return
      }

      this.guideKeys.push(data.id)

      if (data.measurements_in) {
        data.measurements_in = await this.fetchData(data.measurements_in)
      }

      if (data.measurements_cm) {
        data.measurements_cm = await this.fetchData(data.measurements_cm)
      }

      this.guides.push(data)
    },

    /**
     * Fetch and parse content.
     * @param {String} url
     * @returns {Object}
     */
    async fetchData(url) {
      let data

      await fetch(url)
        .then(response => response.text())
        .then(text => {
          data = Papa.parse(text)
        })
        // eslint-disable-next-line no-console
        .catch(err => console.error(err))

      return data
    },

    /**
     * Handle opening Gorgias chat.
     */
    openChat(event) {
      event.preventDefault()

      const GorgiasChat = window.GorgiasChat || false

      if (!GorgiasChat) {
        return
      }

      GorgiasChat.open()
    },
  },
}
</script>

<style lang="scss">
@import './size-guide';
</style>
